import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { employerAPI, getProxiedLogoUrl } from '../services/api';

const Login = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [branding, setBranding] = useState({ companyName: '', companyLogoUrl: '' });
  const [brandingLoaded, setBrandingLoaded] = useState(false);
  const [brandingError, setBrandingError] = useState(null);
  const apiBaseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');
  const orgId = new URLSearchParams(location.search).get('org');

  useEffect(() => {
    let isMounted = true;

    const loadBranding = async () => {
      if (!orgId) {
        if (isMounted) {
          setBranding({ companyName: '', companyLogoUrl: '' });
          setBrandingError(null);
          setBrandingLoaded(true);
        }
        return;
      }

      setBrandingLoaded(false);
      setBrandingError(null);

      try {
        const response = await employerAPI.getEmployerById(orgId);
        if (!isMounted) return;
        const employer = response.data || {};
        const logoPath = employer.companyLogoUrl || employer.company_logo_url || '';
        // Use proxied logo URL to avoid CORS issues
        const resolvedLogo = logoPath ? getProxiedLogoUrl(logoPath, apiBaseUrl) : '';
        setBranding({
          companyName: employer.companyName || employer.company_name || '',
          companyLogoUrl: resolvedLogo,
        });
      } catch (error) {
        console.error('Failed to load employer branding:', error);
        if (!isMounted) return;
        setBranding({
          companyName: '',
          companyLogoUrl: '',
        });
        setBrandingError('Unable to load company branding.');
        toast.error('Unable to load company branding.');
      } finally {
        if (isMounted) {
          setBrandingLoaded(true);
        }
      }
    };

    loadBranding();

    return () => {
      isMounted = false;
    };
  }, [orgId, apiBaseUrl]);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      
      // Dismiss any existing toasts
      toast.dismiss();
      
      const result = await login(data.email, data.password);
      
      if (result.success) {
        // Check user type and validate login page access
        const userType = result.user?.userType || result.user?.user_type;
        const isEmployer = userType === 'employer' || userType === 'employer_staff';
        const isCandidate = userType === 'candidate';
        const isAdmin = userType === 'admin';
        
        // If employer is trying to login without orgId (general login page), block them
        if (isEmployer && !orgId) {
          // Get employer ID from user object to construct login URL
          console.log('Employer login detected, user object:', result.user);
          const employerId = result.user?.employerId || result.user?.employer_id;
          console.log('Extracted employerId:', employerId);
          const frontendUrl = window.location.origin;
          const loginUrl = employerId ? `${frontendUrl}/login?org=${employerId}` : null;
          console.log('Constructed loginUrl:', loginUrl);
          
          // Clear the login session
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          
          // Show error with login URL if available
          if (loginUrl) {
            const message = `Employers must use their organization-specific login link.\n\nYour login URL:\n${loginUrl}\n\nClick the toast to go to your login page.`;
            toast.error(message, {
              duration: 15000,
              style: { 
                maxWidth: '600px', 
                whiteSpace: 'pre-line',
                wordBreak: 'break-all',
                fontSize: '14px',
                lineHeight: '1.6'
              },
              onClick: () => {
                window.location.href = loginUrl;
              }
            });
          } else {
            console.warn('No employerId found in user object, cannot construct login URL');
            toast.error('Employers must use their organization-specific login link. Please contact your administrator for the correct login URL.', {
              duration: 6000
            });
          }
          setIsLoading(false);
          return;
        }
        
        // If candidate/admin is trying to login through employer login page (with orgId), block them
        if ((isCandidate || isAdmin) && orgId) {
          // Clear the login session
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          
          toast.error('Please use the general login page to access your account.', {
            duration: 5000
          });
          setIsLoading(false);
          // Redirect to general login page
          navigate('/login');
          return;
        }
        
        // Check if password change is required
        if (result.mustChangePassword) {
          toast.success('Login successful. Please change your password.', {
            duration: 3000
          });
          navigate('/change-password');
          return;
        }
        
        toast.success(`Welcome back, ${result.user.firstName}!`, {
          duration: 3000
        });
        navigate('/dashboard');
      } else {
        const errorMsg = result.error || 'Invalid credentials. Please try again.';
        toast.error(errorMsg, {
          duration: 5000
        });
        
        // If error mentions email verification, show link to verification page
        if (errorMsg.toLowerCase().includes('verify') || errorMsg.toLowerCase().includes('verification')) {
          setTimeout(() => {
            const email = document.querySelector('#email')?.value;
            if (email) {
              navigate('/verify-email', { state: { email } });
            }
          }, 2000);
        }
        
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Unexpected error during login:', error);
      toast.error('An unexpected error occurred. Please try again.', {
        duration: 5000
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          {branding.companyLogoUrl ? (
            <img
              src={branding.companyLogoUrl}
              alt={`${branding.companyName} logo`}
              className="mx-auto h-16 w-auto mb-6"
              onError={(e) => {
                console.error('Failed to load company logo:', branding.companyLogoUrl);
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <img
              src={`${process.env.PUBLIC_URL}/liaizn-logo.png`}
              alt="Liaizn"
              className="mx-auto h-14 w-auto mb-6 object-contain"
            />
          )}
          <h1 className="text-2xl font-bold text-secondary-900">
            {branding.companyName
              ? `Sign in to your ${branding.companyName} employer account`
              : 'Sign in to your account'}
          </h1>
          {branding.companyName && (
            <p className="mt-6 text-sm text-secondary-600">
              Use the personalised login link provided to your organisation to continue.
            </p>
          )}
          {orgId && !brandingLoaded && !brandingError && (
            <p className="mt-2 text-sm text-gray-500">Loading organisation branding...</p>
          )}
          {brandingError && (
            <p className="mt-2 text-sm text-danger-600">{brandingError}</p>
          )}
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="auth-card">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="email" className="form-label">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className={`input-field ${errors.email ? 'border-red-500' : ''}`}
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address'
                  }
                })}
              />
              {errors.email && (
                <p className="form-error">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className={`input-field pr-10 ${errors.password ? 'border-red-500' : ''}`}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="form-error">{errors.password.message}</p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <Link 
                  to={orgId ? `/forgot-password?org=${orgId}` : "/forgot-password"} 
                  className="font-medium text-info-700 hover:text-info-600"
                >
                  Forgot your password?
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full btn-primary py-3 text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>

          {!branding.companyName && (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">New to Liaizn?</span>
                </div>
              </div>

              <div className="mt-6">
                <Link
                  to="/register"
                  className="w-full btn-secondary"
                >
                  Create your account
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer Note */}
      <div className="mt-8 text-center pb-8">
        <p className="text-sm text-secondary-500">
          AI-Driven training and recruitment platform
        </p>
      </div>
    </div>
  );
};

export default Login;
